import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { X, CheckCircle2, Send, MessageCircle, Sparkles, Wallet, ShoppingBag, ShieldCheck, Heart, UserCheck } from "lucide-react";

const EARN_POINTS = [
  { icon: UserCheck, title: "Create Your Profile", text: "Complete your profile with your basic information and genuine photos." },
  { icon: Heart, title: "Connect & Date", text: "Discover other users, connect with matches, and build genuine connections." },
  { icon: Sparkles, title: "Earn Through Eligible Activities", text: "Eligible users can earn by taking part in qualifying dating-related activities on GulMatch." },
  { icon: Wallet, title: "Payments & Benefits", text: "Eligible users may receive payments or other benefits according to the platform's rules." },
  { icon: ShoppingBag, title: "Shopping Benefits", text: "Enjoy special shopping-related benefits and opportunities available to eligible users." },
  { icon: ShieldCheck, title: "Follow the Conditions", text: "Eligibility, payments, and earning opportunities are subject to GulMatch's terms, rules, and conditions." },
];

const LEARN_ITEMS = [
  "How the earning process works",
  "Eligibility requirements",
  "Available activities",
  "Payment process",
  "Rules and conditions",
  "Any available benefits",
];

export default function SupportModal({ onClose }) {
  const [links, setLinks] = useState({ telegram_link: "https://t.me/partner_gulbarga", whatsapp_link: "https://wa.me/918867648302" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/support/config");
        setLinks(data);
      } catch {}
    })();
  }, []);

  const track = (channel) => {
    api.post("/support/track", { channel }).catch(() => {});
  };

  return (
    <div className="absolute inset-0 z-[70] flex flex-col" data-testid="support-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="support-backdrop" />
      <div className="relative mt-auto max-h-[92%] bg-[#15121E] border-t border-white/10 rounded-t-3xl animate-float-up flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative px-5 pt-6 pb-4 shrink-0 bg-gradient-to-b from-rose-500/15 to-transparent">
          <button data-testid="support-close-button" onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 grid place-items-center text-slate-300 active:scale-90 transition-transform z-10">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">💰</span>
            <h2 className="text-2xl font-black text-white leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              How to Earn with GulMatch
            </h2>
          </div>
          <p className="text-sm text-slate-400">Turn genuine connections into real rewards.</p>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto no-scrollbar px-5 pb-6">
          <div className="flex flex-col gap-3">
            {EARN_POINTS.map((p) => (
              <div key={p.title} className="flex items-start gap-3 p-3.5 rounded-2xl bg-white/5 border border-white/8">
                <span className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-rose-500/20 to-amber-400/20 border border-rose-400/20 shrink-0">
                  <p.icon className="w-5 h-5 text-rose-400" />
                </span>
                <div>
                  <p className="text-sm font-bold text-white">{p.title}</p>
                  <p className="text-xs text-slate-400 leading-relaxed mt-0.5">{p.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Contact support */}
          <div className="mt-5 p-4 rounded-2xl bg-gradient-to-b from-rose-500/10 to-transparent border border-rose-400/20">
            <p className="text-sm font-bold text-white mb-1">Want to know how earning works?</p>
            <p className="text-xs text-slate-400 mb-3">Contact our Support Team to learn:</p>
            <ul className="space-y-1.5">
              {LEARN_ITEMS.map((item) => (
                <li key={item} className="flex items-center gap-2 text-xs text-slate-300">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0" />{item}
                </li>
              ))}
            </ul>
          </div>

          {/* Telegram / WhatsApp */}
          <div className="grid grid-cols-2 gap-3 mt-5">
            <a data-testid="support-telegram-button" href={links.telegram_link} target="_blank" rel="noopener noreferrer"
              onClick={() => track("telegram")}
              className="h-12 rounded-2xl bg-[#229ED9] text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-transform shadow-lg shadow-sky-500/20">
              <Send className="w-4 h-4" /> Telegram
            </a>
            <a data-testid="support-whatsapp-button" href={links.whatsapp_link} target="_blank" rel="noopener noreferrer"
              onClick={() => track("whatsapp")}
              className="h-12 rounded-2xl bg-[#25D366] text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-transform shadow-lg shadow-emerald-500/20">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>

          <p className="text-[10px] text-slate-600 text-center mt-4 leading-snug">
            Earnings, eligibility, and benefits are governed by GulMatch's terms & conditions. Support availability may vary.
          </p>
        </div>
      </div>
    </div>
  );
}
